import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    useTheme
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const S3_BUCKET_URL = 'https://get-code-content.s3.amazonaws.com';
const ABOUT_IMAGES_PATH = '/about';

function AboutPage() {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 600);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getImageUrl = (index) => {
        const prefix = isMobile ? 'mobile' : 'desk';
        return `${S3_BUCKET_URL}${ABOUT_IMAGES_PATH}/${prefix}_${index}.png`;
    };

    return (
        <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            backgroundColor: theme.palette.background.default,
            transition: 'background-color 0.3s ease'
        }}>
            <Container 
                maxWidth="lg"
                sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    pt: { xs: 11, sm: 14, md: 16 },
                    pb: { xs: 6, sm: 8 }
                }}
            >
                {/* Hero Header */}
                <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
                    <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.5,
                        mb: 2,
                        borderRadius: '20px',
                        backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 102, 255, 0.08)',
                        color: theme.palette.primary.main,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                    }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        The MyTownCode Vision
                    </Box>

                    <Typography 
                        variant="h1"
                        sx={{ 
                            color: theme.palette.text.primary,
                            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.4rem' },
                            fontWeight: 800,
                            letterSpacing: '-0.03em',
                            mb: 2.5
                        }}
                    >
                        About MyTownCode
                    </Typography>

                    <Typography 
                        variant="body1"
                        sx={{
                            fontSize: { xs: '1.05rem', sm: '1.35rem' },
                            color: theme.palette.text.secondary,
                            maxWidth: '820px',
                            mx: 'auto',
                            lineHeight: 1.6,
                            fontWeight: 400
                        }}
                    >
                        MyTownCode is a groundbreaking platform that revolutionizes the way people connect with products and services, making access easier, faster, and more intuitive.
                    </Typography>
                </Box>

                {/* Content Sections */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 8, sm: 12 } }}>
                    {/* First Section */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 3, sm: 6 }, 
                        alignItems: 'center', 
                    }}>
                        <Box sx={{ 
                            flex: 1,
                            order: { xs: 2, md: 1 }
                        }}>
                            <Typography 
                                variant="h4" 
                                sx={{
                                    fontSize: { xs: '1.4rem', sm: '1.85rem' },
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em',
                                    color: theme.palette.text.primary,
                                    mb: 2
                                }}
                            >
                                Seamless Offline-to-Digital Transition
                            </Typography>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    fontSize: { xs: '0.95rem', sm: '1.15rem' },
                                    lineHeight: 1.7,
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Picture this: you're driving past a billboard, listening to a radio ad, or walking through the city when a short, memorable code catches your attention—something like 'NY3344' or 'SG2023'. You don't have time to remember long URLs or complex names. With MyTownCode, all you need is the code.
                                <br/><br/>
                                Simply enter the code in the MyTownCode app or website, and you'll be instantly directed to the genuine web page of the advertised product or service. Say goodbye to confusing searches and fake links—MyTownCode ensures you're always connected to what matters, quickly and securely.
                            </Typography>
                        </Box>
                        <Box sx={{ 
                            flex: 1, 
                            width: '100%', 
                            height: { xs: 240, sm: 360, md: 400 },
                            borderRadius: { xs: '16px', sm: '22px' },
                            overflow: 'hidden',
                            boxShadow: isDark 
                                ? '0 20px 40px rgba(0, 0, 0, 0.6)' 
                                : '0 16px 36px rgba(15, 23, 42, 0.08)',
                            border: `1px solid ${theme.palette.divider}`,
                            order: { xs: 1, md: 2 }
                        }}>
                            <img
                                src={getImageUrl(1)}
                                alt="MyTownCode billboard demonstration"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Second Section */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 3, sm: 6 }, 
                        alignItems: 'center', 
                    }}>
                        <Box sx={{ 
                            flex: 1, 
                            width: '100%',
                            height: { xs: 240, sm: 360, md: 400 },
                            borderRadius: { xs: '16px', sm: '22px' },
                            overflow: 'hidden',
                            boxShadow: isDark 
                                ? '0 20px 40px rgba(0, 0, 0, 0.6)' 
                                : '0 16px 36px rgba(15, 23, 42, 0.08)',
                            border: `1px solid ${theme.palette.divider}`,
                        }}>
                            <img
                                src={getImageUrl(2)}
                                alt="MyTownCode engagement workspace"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography 
                                variant="h4" 
                                sx={{
                                    fontSize: { xs: '1.4rem', sm: '1.85rem' },
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em',
                                    color: theme.palette.text.primary,
                                    mb: 2
                                }}
                            >
                                Revolutionize Engagement, Amplify Impact
                            </Typography>
                            <Typography 
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '0.95rem', sm: '1.15rem' },
                                    lineHeight: 1.7,
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                MyTownCode is the ultimate tool for advertisers to bridge the gap between attention and action. Replace long and forgettable URLs with simple, custom codes that stick in users' minds. Whether it's a visual ad on a billboard or a radio commercial, MyTownCode ensures your audience can access the advertised page in seconds.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Third Section */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 3, sm: 6 }, 
                        alignItems: 'center', 
                    }}>
                        <Box sx={{ 
                            flex: 1,
                            order: { xs: 2, md: 1 }
                        }}>
                            <Typography 
                                variant="h4" 
                                sx={{
                                    fontSize: { xs: '1.4rem', sm: '1.85rem' },
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em',
                                    color: theme.palette.text.primary,
                                    mb: 2
                                }}
                            >
                                Secure, Reliable, and User-Friendly
                            </Typography>
                            <Typography 
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '0.95rem', sm: '1.15rem' },
                                    lineHeight: 1.7,
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Our platform is built with security at its core. Every code is unique and verified, ensuring users are directed only to legitimate destinations. For businesses, our analytics dashboard provides valuable insights into user engagement and campaign performance.
                            </Typography>
                        </Box>
                        <Box sx={{ 
                            flex: 1, 
                            width: '100%', 
                            height: { xs: 240, sm: 360, md: 400 },
                            borderRadius: { xs: '16px', sm: '22px' },
                            overflow: 'hidden',
                            boxShadow: isDark 
                                ? '0 20px 40px rgba(0, 0, 0, 0.6)' 
                                : '0 16px 36px rgba(15, 23, 42, 0.08)',
                            border: `1px solid ${theme.palette.divider}`,
                            order: { xs: 1, md: 2 }
                        }}>
                            <img
                                src={getImageUrl(3)}
                                alt="MyTownCode security and analytics"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}

export default AboutPage;